import * as ExcelJS from 'exceljs';
import { IConfiguracionLLaves } from '../../../shared/interfaces/database/configuracion';

export enum ColumnType {
    String = 'string',    
    Number = 'number',
    Date = 'date',
    Boolean = 'boolean',
    FormDocument = 'formDocument'
}

export interface IImportResponse {
  valid: boolean;
  msg: string;
}

//#region Implementations

export interface IExcelColumn {
  dataType: ExcelJS.ValueType;
  format: string;
}

export interface IUnique{
  isUnique: boolean;
}

export interface IForeingKey {
  llaveForaneaTablaId: number;
  llaveForaneaColumnaVisible: string;
  llavesForaneas: IConfiguracionLLaves[];
}

export class ColumnTypeChecker {
  static hasExcelColumn(c: any): c is IExcelColumn {
    return 'dataType' in c && 'format' in c;
  }

  static hasUnique(c: any): c is IUnique {
    return 'isUnique' in c;
  }

  static hasForeingKey(c: any): c is IForeingKey {
    return 'llaveForaneaTablaId' in c && 'llavesForaneas' in c;
  }
}

//#endregion  Implementations

//#region Column Classes

export const VisibleColumns = [
    { text: 'Texto', value: ColumnType.String },
    { text: 'Número', value: ColumnType.Number },
    { text: 'Fecha', value: ColumnType.Date },
    { text: 'Booleano', value: ColumnType.Boolean },
    { text: 'Documento de Formulario', value: ColumnType.FormDocument }
];

export abstract class BaseColumn<T extends ColumnType> {
  protected _type: T;
  protected _name: string;
  protected _displayName: string;
  protected _editable: boolean;

  constructor(data: any){
    this._type = data.type;
    this._name = data.name;
    this._displayName = data.displayName;
    this._editable = data.editable;
  }

  get type(): T {
    return this._type;
  }

  set type(value: T) {
    this._type = value;
  }

  get name(): string {
    return this._name;
  }

  set name(value: string) {
    this._name = value;
  }

  get displayName(): string {
    return this._displayName;
  }

  set displayName(value: string) {
    this._displayName = value;
  }

  get editable(): boolean {
    return this._editable ?? true;
  }
  
  set editable(value: boolean) {
    this._editable = value;
  }

  getColumnAlignment(): string {
    return 'text-left'
  } 

  toJSON(): any {
    return {
      type: this._type,
      name: this._name,
      displayName: this._displayName,
      editable: this.editable
    };
  }
}

export class StringColumn extends BaseColumn<ColumnType.String> implements IExcelColumn, IUnique {

  private _dataType: ExcelJS.ValueType
  private _format: string;
  private _isUnique: boolean;

  constructor(data: any){
    super(data);
    this._type = ColumnType.String;
    this._dataType = ExcelJS.ValueType.String;
    this._format = data.format;
    this._isUnique = data.isUnique;
  }

  get dataType(): ExcelJS.ValueType {
    return this._dataType;
  }

  get format(): string {
    return this._format;
  }

  set format(value: string) {
    this._format = value;
  }

  get isUnique(): boolean {
    return this._isUnique ?? false;
  }

  set isUnique(value: boolean) {
    this._isUnique = value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      dataType: this._dataType,
      format: this._format,
      isUnique: this._isUnique
    };
  }
}

export class NumberColumn extends BaseColumn<ColumnType.Number> implements IExcelColumn, IUnique, IForeingKey {

  private _dataType: ExcelJS.ValueType
  private _format: string;
  private _isUnique: boolean;

  private _llaveForaneaTablaId: number;
  private _llavesForaneas: IConfiguracionLLaves[];
  private _llaveForaneaColumnaVisible: string;

  constructor(data: any){
    super(data);
    this._type = ColumnType.Number;
    this._dataType = ExcelJS.ValueType.Number;
    this._format = data.format;
    this._isUnique = data.isUnique;
    this._llaveForaneaTablaId = data.llaveForaneaTablaId;
    this._llavesForaneas = data.llavesForaneas;
    this._llaveForaneaColumnaVisible = data.llaveForaneaColumnaVisible;
  }

  get dataType(): ExcelJS.ValueType {
    return this._dataType;
  }

  get format(): string {
    return this._format;
  }

  set format(value: string) {
    this._format = value;
  }

  get isUnique(): boolean {
    return this._isUnique ?? false;
  }

  set isUnique(value: boolean) {
    this._isUnique = value;
  }

  get llaveForaneaTablaId(): number {
    return this._llaveForaneaTablaId;
  }
  
  set llaveForaneaTablaId(value: number) {
    this._llaveForaneaTablaId = value;
  }

  get llavesForaneas(): IConfiguracionLLaves[] {
    return this._llavesForaneas;
  }

  set llavesForaneas(value: IConfiguracionLLaves[]) {
    this._llavesForaneas = value;
  }

  get llaveForaneaColumnaVisible(): string {
    return this._llaveForaneaColumnaVisible;
  }

  set llaveForaneaColumnaVisible(value: string) {
    this._llaveForaneaColumnaVisible = value;
  }

  override getColumnAlignment(): string {
    if (this.format && /[$€£¥]/.test(this.format)) {
      return 'text-right';
    }
    return 'text-center';
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      dataType: this._dataType,
      format: this._format,
      isUnique: this._isUnique,
      llaveForaneaTablaId: this._llaveForaneaTablaId,
      llaveForaneaColumnaVisible: this._llaveForaneaColumnaVisible,
      llavesForaneas: this._llavesForaneas
    };
  }
}

export class DateColumn extends BaseColumn<ColumnType.Date> implements IExcelColumn {

  private _dataType: ExcelJS.ValueType
  private _format: string;

  constructor(data: any){
    super(data);
    this._type = ColumnType.Date;
    this._dataType = ExcelJS.ValueType.Date;
    this._format = data.format;
  }

  get dataType(): ExcelJS.ValueType {
    return this._dataType;
  }

  get format(): string {
    return this._format;
  }

  set format(value: string) {
    this._format = value;
  }

  override getColumnAlignment(): string {
    return 'text-center';
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      dataType: this._dataType,
      format: this._format
    };
  }
}

export class BooleanColumn extends BaseColumn<ColumnType.Boolean> implements IExcelColumn {

  private _dataType: ExcelJS.ValueType
  private _format: string;

  constructor(data: any){
    super(data);
    this._type = ColumnType.Boolean;
    this._dataType = ExcelJS.ValueType.Boolean;
    this._format = data.format;
  }

  get dataType(): ExcelJS.ValueType {
    return this._dataType;
  }

  get format(): string {
    return this._format;
  }

  set format(value: string) {
    this._format = value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      dataType: this._dataType,
      format: this._format
    };
  }
}

export class FormDocumentColumn extends BaseColumn<ColumnType.FormDocument> {

  constructor(data: any){
    super(data);
    this._type = ColumnType.FormDocument;
  }
}

//#endregion Column Classes
